import { View, Text, ScrollView, Platform } from 'react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { trpc } from '../../lib/trpc';
import {
  BrandSwitch,
  Card,
  LoadingSpinner,
  ErrorState,
  PageContainer,
  PageHeader,
  Badge,
  Button,
  WebHead,
} from '../../components';
import { useColors } from '../../lib/colors';
import { webPrivacyProps } from '../../lib/privacy';
import { formatCurrency } from '../../lib/utils';
import type { RecurringStreamItem } from '@perdiem/trpc';

function frequencyLabel(f: RecurringStreamItem['frequency']): string {
  switch (f) {
    case 'WEEKLY': return 'Weekly';
    case 'BIWEEKLY': return 'Biweekly';
    case 'SEMI_MONTHLY': return 'Twice monthly';
    case 'MONTHLY': return 'Monthly';
    case 'ANNUALLY': return 'Annually';
    default: return '—';
  }
}

export default function RecurringScreen() {
  const c = useColors();
  const utils = trpc.useUtils();
  const [showSyncTooltip, setShowSyncTooltip] = useState(false);
  const [optimisticExcludedById, setOptimisticExcludedById] = useState<Record<string, boolean>>({});
  const updateVersionByIdRef = useRef<Record<string, number>>({});
  const isWeb = Platform.OS === 'web';

  const listQ = trpc.recurring.list.useQuery();
  const perDiemQ = trpc.budget.perDiem.useQuery();
  const updateMutation = trpc.recurring.update.useMutation();
  const syncMutation = trpc.recurring.syncNow.useMutation({
    onSuccess: () => {
      void utils.recurring.list.invalidate();
      void utils.budget.perDiem.invalidate();
    },
  });

  const streams = listQ.data ?? [];
  const streamsById = useMemo(
    () => new Map(streams.map((stream) => [stream.id, stream])),
    [streams],
  );

  useEffect(() => {
    setOptimisticExcludedById((current) => {
      let changed = false;
      const next = { ...current };

      for (const stream of streams) {
        if (next[stream.id] === stream.excludeFromPerDiem) {
          delete next[stream.id];
          changed = true;
        }
      }

      return changed ? next : current;
    });
  }, [streams]);

  const effectiveStreams = useMemo(() => {
    return streams.map((stream) => ({
      ...stream,
      excludeFromPerDiem: optimisticExcludedById[stream.id] ?? stream.excludeFromPerDiem,
    }));
  }, [optimisticExcludedById, streams]);

  const visible = useMemo(() => {
    return effectiveStreams.filter((s) => s.isActive);
  }, [effectiveStreams]);

  const reservedMonthly = useMemo(() => {
    return effectiveStreams
      .filter((s) => s.direction === 'outflow' && s.isActive && s.excludeFromPerDiem)
      .reduce((sum, s) => sum + s.monthlyReservedAmount, 0);
  }, [effectiveStreams]);

  const handleToggle = (id: string, excludeFromPerDiem: boolean) => {
    const previousValue = optimisticExcludedById[id] ?? streamsById.get(id)?.excludeFromPerDiem ?? false;
    const nextVersion = (updateVersionByIdRef.current[id] ?? 0) + 1;
    updateVersionByIdRef.current[id] = nextVersion;

    setOptimisticExcludedById((current) => ({
      ...current,
      [id]: excludeFromPerDiem,
    }));

    updateMutation.mutate(
      { id, excludeFromPerDiem },
      {
        onError: () => {
          if (updateVersionByIdRef.current[id] !== nextVersion) return;
          setOptimisticExcludedById((current) => ({
            ...current,
            [id]: previousValue,
          }));
        },
        onSettled: () => {
          if (updateVersionByIdRef.current[id] !== nextVersion) return;
          void utils.recurring.list.invalidate();
          void utils.budget.perDiem.invalidate();
        },
      },
    );
  };

  if (listQ.isLoading) return <LoadingSpinner message="Loading recurring streams..." />;
  if (listQ.error) {
    return (
      <ErrorState
        error={listQ.error}
        screen="recurring"
        metadata={{ failedQueries: 'recurring.list' }}
        onRetry={listQ.refetch}
      />
    );
  }

  return (
    <>
      <WebHead><title>Recurring – Per Diem</title></WebHead>
      <View className="flex-1 bg-canvas dark:bg-canvas-dark">
        <PageContainer className="flex-1">
          <PageHeader title="Recurring" closeTo="/(app)/budget" />

          <Text className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Exclude large, predictable charges from your per diem so the daily
            number stays steady across the month. Excluded streams still show
            up in your budget — they just don&rsquo;t distort your day-to-day
            discretionary spending.
          </Text>

          <Card className="relative z-20 overflow-visible flex-row items-center justify-between mb-4 px-4 py-3">
            <View className="flex-1">
              <Text className="text-xs text-gray-500 dark:text-gray-400">
                Per diem
              </Text>
              <Text
                className="text-xl font-semibold text-gray-900 dark:text-gray-100"
                {...webPrivacyProps('financial')}
              >
                {perDiemQ.data
                  ? formatCurrency(perDiemQ.data.perDiem, { decimals: 0 })
                  : '—'}
                <Text className="text-xs font-normal text-gray-500 dark:text-gray-400">
                  {' '}/ day
                </Text>
              </Text>
            </View>
            <View className="flex-1">
              <Text className="text-xs text-gray-500 dark:text-gray-400">
                Excluded from per diem
              </Text>
              <Text
                className="text-xl font-semibold text-gray-900 dark:text-gray-100"
                {...webPrivacyProps('financial')}
              >
                {formatCurrency(reservedMonthly, { decimals: 0 })}
                <Text className="text-xs font-normal text-gray-500 dark:text-gray-400">
                  {' '}/ mo
                </Text>
              </Text>
            </View>
            <View className="relative items-end">
              <Button
                variant="ghost"
                size="sm"
                loading={syncMutation.isPending}
                onPress={() => syncMutation.mutate()}
                onHoverIn={isWeb ? () => setShowSyncTooltip(true) : undefined}
                onHoverOut={isWeb ? () => setShowSyncTooltip(false) : undefined}
                onFocus={() => setShowSyncTooltip(true)}
                onBlur={() => setShowSyncTooltip(false)}
                accessibilityLabel="Sync recurring streams from Plaid"
              >
                Sync
              </Button>
              {showSyncTooltip && (
                <View
                  pointerEvents="none"
                  className="absolute right-0 top-full z-10 mt-2 w-64 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                >
                  <Text className="text-[11px] leading-4 text-gray-700 dark:text-gray-200">
                    Refreshes recurring streams from Plaid and relinks matching transactions. It does not run a full account sync.
                  </Text>
                </View>
              )}
            </View>
          </Card>

          {visible.length === 0 ? (
            <Card className="items-center py-8">
              <Ionicons name="repeat-outline" size={32} color={c.textSecondary} />
              <Text className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                No discretionary recurring streams detected yet.
              </Text>
              <Text className="text-xs text-gray-400 dark:text-gray-500 mt-1 text-center px-8">
                We only show discretionary outflows here. Tap Sync to refresh now.
              </Text>
            </Card>
          ) : (
            <Card className="relative z-0 flex-1 p-0 overflow-hidden">
              <ScrollView className="flex-1">
                {visible.map((s, i) => (
                  <View
                    key={s.id}
                    className={`px-4 py-3 ${i > 0 ? 'border-t border-gray-100 dark:border-gray-700' : ''}`}
                  >
                    <View className="flex-row items-start justify-between">
                      <View className="flex-1 pr-3">
                        <Text
                          className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                          numberOfLines={1}
                          {...webPrivacyProps('private')}
                        >
                          {s.merchantName ?? s.description ?? 'Unknown'}
                        </Text>
                        <View className="flex-row flex-wrap items-center gap-2 mt-1">
                          <Text
                            className="text-xs text-gray-500 dark:text-gray-400"
                            {...webPrivacyProps('financial')}
                          >
                            {frequencyLabel(s.frequency)} · avg{' '}
                            {formatCurrency(s.averageAmount ?? 0, { decimals: 0 })}
                          </Text>
                          {s.lastAmount != null && s.lastDate && (
                            <Text
                              className="text-xs text-gray-400 dark:text-gray-500"
                              {...webPrivacyProps('financial')}
                            >
                              · last {formatCurrency(s.lastAmount, { decimals: 0 })} on{' '}
                              {s.lastDate}
                            </Text>
                          )}
                        </View>
                        <View className="flex-row flex-wrap items-center gap-2 mt-1">
                          {s.institutionName && (
                            <Badge label={s.institutionName} variant="gray" />
                          )}
                          {s.status === 'EARLY_DETECTION' && (
                            <Badge label="Early detection" variant="yellow" />
                          )}
                        </View>
                        {s.excludeFromPerDiem && (
                          <Text
                            className="text-xs text-forest-700 dark:text-forest-500 mt-1"
                            {...webPrivacyProps('financial')}
                          >
                            Reserving {formatCurrency(s.monthlyReservedAmount, { decimals: 0 })} / mo
                          </Text>
                        )}
                      </View>

                      <View className="items-end">
                        <BrandSwitch
                          value={s.excludeFromPerDiem}
                          onValueChange={(value) => handleToggle(s.id, value)}
                          accessibilityLabel="Exclude from per diem"
                        />
                        <Text className="text-[10px] text-gray-400 mt-1">
                          Exclude
                        </Text>
                      </View>
                    </View>
                  </View>
                ))}
              </ScrollView>
            </Card>
          )}
        </PageContainer>
      </View>
    </>
  );
}
